'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { AddressType } from '@/types/address';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface AddressDialogProps {
  isLoading?: boolean;
  dialogTitle: string;
  address: AddressType;
  setAddress: (address: AddressType) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  children: React.ReactNode;
}

export default function AddressDialog({
  isLoading = false,
  dialogTitle,
  address,
  setAddress,
  open,
  setOpen,
  children,
}: AddressDialogProps) {
  // Create local state to manage form values
  const [localAddress, setLocalAddress] = useState<AddressType>(address);

  // Update local state when address prop changes
  useEffect(() => {
    setLocalAddress(address);
  }, [address]);

  // Handle saving address changes without submitting the parent form
  const handleSaveAddress = (e: React.MouseEvent) => {
    e.preventDefault();
    // Update parent state with local state
    setAddress({
      ...localAddress,
      // Update the formatted address for display
      formattedAddress: `${
        localAddress.address1 +
        (localAddress.address2 ? `, ${localAddress.address2}` : '')
      }, ${localAddress.city}, ${localAddress.region} ${localAddress.postalCode}${localAddress.country ? `,·${localAddress.country}` : ''}`,
    });
    setOpen(false);
  };

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>Edit the address details below</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="address1">Address Line 1</Label>
            <Input
              id="address1"
              name="address1"
              value={localAddress.address1}
              onChange={handleChange}
              placeholder="Street address"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address2">Address Line 2</Label>
            <Input
              id="address2"
              name="address2"
              value={localAddress.address2}
              onChange={handleChange}
              placeholder="Apt, Suite, Unit, etc."
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                name="city"
                value={localAddress.city}
                onChange={handleChange}
                placeholder="City"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="region">State/Province</Label>
              <Input
                id="region"
                name="region"
                value={localAddress.region}
                onChange={handleChange}
                placeholder="State/Province"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="postalCode">Postal Code</Label>
              <Input
                id="postalCode"
                name="postalCode"
                value={localAddress.postalCode}
                onChange={handleChange}
                placeholder="Postal code"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                name="country"
                value={localAddress.country}
                onChange={handleChange}
                placeholder="Country"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Hidden fields for lat/lng coordinates that stay the same */}
          <input type="hidden" name="lat" value={localAddress.lat} />
          <input type="hidden" name="lng" value={localAddress.lng} />

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveAddress}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
